import { FilterOutlined, SearchOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import { makeStyles } from '@material-ui/styles';
import { Icon, Modal, Spin, Table, Tabs, Tag, Tooltip, message } from 'antd';
import classNames from 'classnames';
import uniqBy from 'lodash/uniqBy';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  TableAsyncSelect,
  TableSearchDate,
  TableSearchInput,
} from 'common/components/table-filter-component';
import { bookingJourneyType } from 'common/constants/booking';
import {
  PayoutEmpty as PayoutEmptyIcon,
  PayoutListBtnSettlement as PayoutLIstBtnSettlementIcon,
  PayoutListActionAdd as PayoutListActionAddIcon,
  PayoutListActionCancel as PayoutListActionCancelIcon,
  PayoutListActionRemove as PayoutListActionRemoveIcon,
} from 'common/components/svgs';
import { sortDirectionMapping } from '~constants';
import { formatPriceWithSpace } from '~helpers/currency';
import { dayFormatType, formatDay } from '~helpers/day';
import { capitalizeFirstLetter } from '~helpers/jstool';
import {
  MUTATION_AUDIT_PAYOUT,
  QUERY_COUNTRY,
  QUERY_PARTNER_PAYOUT,
  QUERY_SETTLEMENT_INFO,
} from '~pages/dashboard/referral/payout/queries';
import { QUERY_LIST_PARTNERS } from '~pages/dashboard/referral/partner-account/queries';
import generatePath from '~settings/routing';
import useExportPayout from './export';
import PayoutCancelReasonEditor from './payout-cancel-modal/index';
import PayoutOppsViewer from './payout-detail-viewer-opps/index';
import SettlementList from './settlement-list';

export const Statuses = {
  New: 'NEW',
  Pending: 'PENDING_APPROVAL',
  Approved: 'APPROVED',
  Cancelled: 'CANCELLED',
  Settled: 'SETTLED',
  Invalid: 'INVALID',
  Deducted: 'DEDUCTED',
};

export const SecondaryStatus = {
  BookingCancelled: 'BOOKING_CANCELLED',
  WithoutBookingCommission: 'WITHOUT_BOOKING_COMMISSION',
  ManualCancel: 'MANUAL_CANCEL',
  ManualDeduct: 'MANUAL_DEDUCT',
};

export const SecondaryStatusLabel = {
  BookingCancelled: 'cms.referral.payout.list.column.secondary_status.booking_cancelled',
  WithoutBookingCommission:
    'cms.referral.payout.list.column.secondary_status.without_booking_commission',
  ManualCancel: 'cms.referral.payout.list.column.secondary_status.manual_cancel',
  ManualDeduct: 'cms.referral.payout.list.column.secondary_status.manual_deduct',
};

const AllStatus = [];
const PendingStatus = [Statuses.Pending, Statuses.Approved];
const HistoryStatus = [Statuses.Settled];

const StatusesEnum = {
  all: AllStatus,
  pending: PendingStatus,
  history: HistoryStatus,
};

export const OperationType = {
  Approve: 'APPROVE',
  Unapproved: 'UNAPPROVE',
};

const PayoutRecordSortBy = {
  CreatedAt: 'CREATED_AT',
  UpdatedAt: 'UPDATED_AT',
  Amount: 'AMOUNT',
};

const useStyles = makeStyles(
  theme => ({
    tagNew: {
      textTransform: 'capitalize',
      background: theme.colors.green1,
      border: '1px solid',
      borderColor: theme.colors.green3,
      color: theme.colors.green6,
    },
    tagPending: {
      textTransform: 'capitalize',
      background: theme.colors.blue1,
      border: '1px solid',
      borderColor: theme.colors.blue4,
      color: theme.colors.blue6,
    },
    tagApproved: {
      textTransform: 'capitalize',
      background: theme.colors.orange1,
      border: '1px solid',
      borderColor: theme.colors.orange3,
      color: theme.colors.orange6,
    },
    tagSuccess: {
      textTransform: 'capitalize',
      background: theme.colors.mainGreen1,
      border: '1px solid',
      borderColor: theme.colors.mainGreen3,
      color: theme.colors.mainGreen6,
    },
    tagFailed: {
      textTransform: 'capitalize',
      background: '#FFF1F0',
      border: '1px solid',
      borderColor: '#FFA39E',
      color: theme.colors.red11,
    },
    tagInvalid: {
      textTransform: 'capitalize',
      background: theme.colors.gray2,
      border: '1px solid',
      borderColor: theme.colors.gray5,
      color: 'rgba(0, 0, 0, 0.45)',
    },
  }),
  { name: 'PartnerPayoutStatusTags' },
);

const Partner = ({ role }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const selectedRowsRaw = useRef([]);

  const [selectTab, setSelectTab] = useState('all');
  const [selectedRows, setSelectedRows] = useState([]);

  const [showSettlementListDrawer, setShowSettlementListDrawer] = useState(false);
  const [showPayoutOppsViewer, setShowPayoutOppsViewer] = useState(null);
  const [showPayoutCancelReasonEditor, setShowPayoutCancelReasonEditor] = useState(null);

  const [showPartnerIdFilter, setShowPartnerIdFilter] = useState(false);
  const [showPartnerCodeFilter, setShowPartnerCodeFilter] = useState(false);
  const [showOppsNumberFilter, setShowOppsNumberFilter] = useState(false);
  const [showUpdatedTimeFilter, setShowUpdatedTimeFilter] = useState(false);
  const [countryListForFilter, setCountryListForFilter] = useState([]);
  const [searchPartnerName, setSearchPartnerName] = useState(null);
  const [queryFilter, setQueryFilter] = useState({
    partnerId: [],
    partnerCode: null,
    oppsNumber: null,
    status: [],
    secondaryStatus: [],
    updatedTime: [],
    sortBy: PayoutRecordSortBy.UpdatedAt,
    sortDirection: 'DESCENDING',
    currencies: [],
    countryIds: [],
    paymentPreference: null,
    stage: null,
  });

  const isValidFilter = useMemo(() => {
    return (
      queryFilter.partnerId.length ||
      queryFilter.partnerCode ||
      queryFilter.oppsNumber ||
      queryFilter.status.length ||
      queryFilter.secondaryStatus.length ||
      queryFilter.updatedTime.length ||
      queryFilter.currencies.length ||
      queryFilter.countryIds.length ||
      queryFilter.stage ||
      queryFilter.paymentPreference
    );
  }, [queryFilter]);

  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
  });

  const {
    loading,
    data: payoutListResponse,
    refetch: refetchPayoutList,
  } = useQuery(QUERY_PARTNER_PAYOUT, {
    variables: {
      pageNumber: pagination.pageNumber,
      pageSize: pagination.pageSize,
      sortBy: queryFilter.sortBy,
      sortDirection: queryFilter.sortDirection,
      statuses: [...new Set(StatusesEnum[selectTab].concat(queryFilter.status))],
      secondaryStatuses: queryFilter.secondaryStatus,
      partnerIds: queryFilter.partnerId.length ? queryFilter.partnerId : null,
      partnerCodes: queryFilter.partnerCode ? [queryFilter.partnerCode] : null,
      bookingReferenceIds: queryFilter.oppsNumber ? [queryFilter.oppsNumber] : null,
      updatedAtMin: queryFilter.updatedTime.length ? queryFilter.updatedTime[0] : null,
      updatedAtMax: queryFilter.updatedTime.length ? queryFilter.updatedTime[1] : null,
      currencies: queryFilter.currencies.length ? queryFilter.currencies : null,
      countryIds: queryFilter.countryIds.length ? queryFilter.countryIds : null,
      paymentPreference: queryFilter?.paymentPreference?.length
        ? queryFilter?.paymentPreference[0]
        : null,
      stages: queryFilter?.stage,
    },
    fetchPolicy: 'cache-and-network',
    onError: e => {
      message.error(e?.toString());
    },
  });

  useQuery(QUERY_COUNTRY, {
    variables: {
      pageNumber: 1,
      pageSize: 20,
      countrySlugs: ['cn', 'in', 'sg', 'id', 'my', 'th'],
    },
    onCompleted: res => {
      const data = res?.countries?.edges;
      setCountryListForFilter(
        data?.map(item => ({ text: item.node.name, value: item.node.id })) || [],
      );
    },
    onError: e => {
      message.error(e?.toString());
    },
  });

  const { exportToCSV, isLoading: isExportPayoutLoading } = useExportPayout();

  const { loading: partnerListLoading, data: partnerListResponse } = useQuery(QUERY_LIST_PARTNERS, {
    variables: {
      name: searchPartnerName || null,
    },
  });

  const partnerList = useMemo(() => {
    return (
      partnerListResponse?.listPartners?.edges.map(edge => ({
        label: edge.node.name || t('cms.referral.payout.list.filter.no_name'),
        value: edge.node.id,
      })) || []
    );
  }, [partnerListResponse]);

  const [auditPayout] = useMutation(MUTATION_AUDIT_PAYOUT, {
    onError: error => {
      message.error(error.message);
      refetchPayoutList();
    },
    refetchQueries: [QUERY_PARTNER_PAYOUT, QUERY_SETTLEMENT_INFO],
  });

  const payoutList = useMemo(() => {
    return payoutListResponse?.listPartnerPayoutRecords?.edges.map(edge => edge.node) || [];
  }, [payoutListResponse]);

  const payoutListTotalCount = useMemo(() => {
    return payoutListResponse?.listPartnerPayoutRecords?.totalCount || 0;
  }, [payoutListResponse]);

  useEffect(() => {
    if (
      payoutList.length === 0 &&
      payoutListResponse?.listPartnerPayoutRecords?.pageInfo?.hasPreviousPage
    ) {
      setPagination(current => ({ ...current, pageNumber: current.pageNumber - 1 }));
    }
  }, [payoutList]);

  const handleRowSelect = (selectedIds, selectedItems) => {
    const waitRemoveApprovePayouts = payoutList
      .filter(payout => !selectedIds.includes(payout.id))
      .map(item => item.id);
    selectedRowsRaw.current = uniqBy(
      selectedRowsRaw.current
        .concat(selectedItems)
        .filter(item => !waitRemoveApprovePayouts.includes(item.id)),
      'id',
    );
    setSelectedRows(current => [
      ...new Set(
        current.concat(selectedIds).filter(item => !waitRemoveApprovePayouts.includes(item)),
      ),
    ]);
  };

  const handleTableChange = key => {
    setPagination(current => ({ ...current, pageNumber: 1 }));
    setQueryFilter({
      partnerId: [],
      partnerCode: null,
      oppsNumber: null,
      status: [],
      secondaryStatus: [],
      updatedTime: [],
      sortBy: PayoutRecordSortBy.UpdatedAt,
      sortDirection: 'DESCENDING',
      currencies: [],
      countryIds: [],
      paymentPreference: null,
      stage: [],
    });
    setSelectTab(key);
  };

  const tableColumns = [
    {
      title: t('cms.referral.payout.list.column.name'),
      dataIndex: 'partner.name',
      key: 'name',
      fixed: 'left',
      width: 150,
      className: 'payout-list__body__list__column',
      render: (text, record) => (
        <span
          title={text}
          className="payout-list__body__list__column-body payout-list__body__list__column-body--color-keppel"
        >
          <span
            className=" payout-list__body__list__column--cursor-pointer"
            onClick={() =>
              history.push(generatePath('referral.partnerDetail', { id: record.partner.code }))
            }
          >
            {text || '-'}
          </span>
        </span>
      ),
      filterDropdown: showPartnerIdFilter ? (
        <TableAsyncSelect
          field="partnerId"
          isMulti
          loading={partnerListLoading}
          value={queryFilter.partnerId || []}
          list={partnerList}
          onInput={value => setSearchPartnerName(value)}
          fetching={partnerListLoading}
          onChange={(_fieldName, value) => {
            const partnerIds = value.length ? value : [];
            setQueryFilter(current => ({ ...current, partnerId: partnerIds }));
            setPagination(current => ({ ...current, pageNumber: 1 }));
            setShowPartnerIdFilter(false);
          }}
        />
      ) : (
        <div />
      ),
      filterIcon: (
        <SearchOutlined style={{ color: queryFilter.partnerId.length ? '#38b2a6' : '#c8c9cb' }} />
      ),
      filterDropdownVisible: showPartnerIdFilter,
      onFilterDropdownVisibleChange: view => {
        setShowPartnerIdFilter(view);
        if (!view) {
          setSearchPartnerName(null);
        }
      },
    },
    {
      title: (
        <div className="payout-list__body__list__column-header">
          {t('cms.referral.payout.list.column.partner_code')}
        </div>
      ),
      dataIndex: 'partner.code',
      key: 'partnerCode',
      width: 150,
      className: 'payout-list__body__list__column',
      render: text => (
        <span title={text} className="payout-list__body__list__column-body">
          {text || '-'}
        </span>
      ),
      filterDropdown: (
        <TableSearchInput
          field="partnerCode"
          value={queryFilter.partnerCode || ''}
          onChange={(_fieldName, value) => {
            setQueryFilter(current => ({ ...current, partnerCode: value }));
            setPagination(current => ({ ...current, pageNumber: 1 }));
            setShowPartnerCodeFilter(false);
          }}
        />
      ),
      filterIcon: (
        <SearchOutlined style={{ color: queryFilter.partnerCode ? '#38b2a6' : '#c8c9cb' }} />
      ),
      filterDropdownVisible: showPartnerCodeFilter,
      onFilterDropdownVisibleChange: view => {
        setShowPartnerCodeFilter(view);
      },
    },
    {
      title: (
        <div className="payout-list__body__list__column-header">
          {t('cms.referral.payout.list.column.currency')}
        </div>
      ),
      dataIndex: 'currency',
      key: 'currency',
      width: 150,
      className: 'payout-list__body__list__column',
      render: text => (
        <span title={text} className="payout-list__body__list__column-body">
          {text || '-'}
        </span>
      ),
      filterMultiple: true,
      filters: [
        { text: 'HKD', value: 'HKD' },
        { text: 'USD', value: 'USD' },
        { text: 'GBP', value: 'GBP' },
        { text: 'AUD', value: 'AUD' },
        { text: 'EUR', value: 'EUR' },
        { text: 'CAD', value: 'CAD' },
        { text: 'NZD', value: 'NZD' },
        { text: 'ZAR', value: 'ZAR' },
        { text: 'JPY', value: 'JPY' },
        { text: 'MXN', value: 'MXN' },
        { text: 'DKK', value: 'DKK' },
        { text: 'CNY', value: 'CNY' },
        { text: 'AED', value: 'AED' },
        { text: 'CHF', value: 'CHF' },
        { text: 'SGD', value: 'SGD' },
        { text: 'PLN', value: 'PLN' },
        { text: 'KRW', value: 'KRW' },
        { text: 'SEK', value: 'SEK' },
        { text: 'COP', value: 'COP' },
        { text: 'CLP', value: 'CLP' },
        { text: 'CZK', value: 'CZK' },
        { text: 'TRY', value: 'TRY' },
      ],
      filteredValue: queryFilter.currencies || [],
      filterIcon: (
        <FilterOutlined style={{ color: queryFilter.currencies.length ? '#38b2a6' : '#c8c9cb' }} />
      ),
    },
    {
      title: (
        <div className="payout-list__body__list__column-header">
          {t('cms.referral.payout.list.column.payout_amount')}
        </div>
      ),
      dataIndex: 'amount',
      key: 'payoutAmount',
      width: 160,
      className: 'payout-list__body__list__column payout-list__body__list__column--w-100',
      render: (text, record) => (
        <span className="payout-list__body__list__column-body">
          {text && record.currency ? `${formatPriceWithSpace(text, record.currency)}` : '-'}
        </span>
      ),
      sorter: true,
      sortOrder:
        (queryFilter.sortBy === 'AMOUNT' && sortDirectionMapping[queryFilter.sortDirection]) ||
        false,
    },
    {
      title: (
        <div className="payout-list__body__list__column-header">
          {t('cms.referral.payout.list.column.referrer_name')}
        </div>
      ),
      dataIndex: 'referrer',
      key: 'referrerName',
      width: 140,
      className: 'payout-list__body__list__column',
      render: (_text, record) => (
        <span className="payout-list__body__list__column-body">
          {record.referrer?.firstName && record.referrer?.lastName
            ? `${record.referrer.firstName} ${record.referrer.lastName}`
            : '-'}
        </span>
      ),
    },
    {
      title: t('cms.referral.payout.list.column.oppos_no'),
      dataIndex: 'bookingReferenceId',
      key: 'oppsNo',
      width: 180,
      className: 'payout-list__body__list__column',
      render: (text, record) => (
        <span className="payout-list__body__list__column-body">
          {text ? (
            <a
              role="button"
              onClick={() =>
                setShowPayoutOppsViewer({
                  id: record.id,
                  bookingReferenceIds: record.bookingReferenceId,
                  bookingJourney: record.bookingJourney,
                })
              }
            >
              {text}
            </a>
          ) : (
            '-'
          )}
        </span>
      ),
      filterDropdown: (
        <TableSearchInput
          field="oppsNumber"
          value={queryFilter.oppsNumber || ''}
          onChange={(_fieldName, value) => {
            setQueryFilter(current => ({ ...current, oppsNumber: value }));
            setPagination(current => ({ ...current, pageNumber: 1 }));
            setShowOppsNumberFilter(false);
          }}
        />
      ),
      filterIcon: (
        <SearchOutlined style={{ color: queryFilter.oppsNumber ? '#38b2a6' : '#c8c9cb' }} />
      ),
      filterDropdownVisible: showOppsNumberFilter,
      onFilterDropdownVisibleChange: view => {
        setShowOppsNumberFilter(view);
      },
    },
    {
      title: t('cms.referral.payout.list.column.country'),
      dataIndex: 'country.name',
      key: 'country',
      className: 'payout-list__body__list__column',
      width: 120,
      filterMultiple: true,
      filters: countryListForFilter,
      filteredValue: queryFilter.countryIds || [],
      filterIcon: (
        <FilterOutlined style={{ color: queryFilter.countryIds.length ? '#38b2a6' : '#c8c9cb' }} />
      ),
      render: text => {
        return <span className="payout-list__body__list__column-body">{text || '-'}</span>;
      },
    },
    {
      title: t('cms.referral.payout.list.column.trigger_point'),
      dataIndex: 'stage',
      className: 'payout-list__body__list__column',
      key: 'stage',
      width: 140,
      render: (text, record) => {
        let resultText = '-';

        if (text === 'BOOKING_COMPLETED') {
          resultText =
            record.bookingJourney === bookingJourneyType.SEMI_AUTOMATIC
              ? 'Order Completed'
              : 'Opp Completed';
        } else if (text === 'BOOKING_PAID') {
          resultText = 'Landlord Paid';
        }

        return <span className="payout-list__body__list__column-body">{resultText}</span>;
      },
      filterMultiple: false,
      filters: [
        { text: 'Completed', value: 'BOOKING_COMPLETED' },
        { text: 'Landlord Paid', value: 'BOOKING_PAID' },
      ],
      filteredValue: queryFilter?.stage,
      filterIcon: (
        <FilterOutlined style={{ color: queryFilter?.stage?.length > 0 ? '#38b2a6' : '#c8c9cb' }} />
      ),
    },
    {
      title: t('cms.referral.payout.list.column.version'),
      dataIndex: 'version',
      className: 'payout-list__body__list__column',
      key: 'version',
      width: 140,
      render: text => {
        return <span className="payout-list__body__list__column-body">{text}</span>;
      },
    },
    {
      title: t('cms.referral.payout.list.column.payment_preference'),
      dataIndex: 'paymentPreference',
      className: 'payout-list__body__list__column',
      key: 'paymentPreference',
      width: 140,
      render: text => {
        return (
          <span className="payout-list__body__list__column-body">
            {capitalizeFirstLetter(text) ?? '-'}
          </span>
        );
      },
      filterMultiple: false,
      filters: [
        { text: 'Online', value: 'ONLINE' },
        { text: 'Offline', value: 'OFFLINE' },
      ],
      filteredValue: queryFilter?.paymentPreference,
      filterIcon: (
        <FilterOutlined
          style={{ color: queryFilter?.paymentPreference?.length > 0 ? '#38b2a6' : '#c8c9cb' }}
        />
      ),
    },
    {
      title: t('cms.referral.payout.list.column.updated_time'),
      dataIndex: 'updatedAt',
      key: 'updateTime',
      width: 160,
      className: 'payout-list__body__list__column',
      render: text => {
        return (
          <span className="payout-list__body__list__column-body">
            {formatDay(dayFormatType.UKWITHHM, text) || '-'}
          </span>
        );
      },
      filterDropdown: (
        <TableSearchDate
          field="updatedTime"
          value={queryFilter.updatedTime || []}
          onChange={(_fieldName, value) => {
            setQueryFilter(current => ({ ...current, updatedTime: value }));
            setPagination(current => ({ ...current, pageNumber: 1 }));
            setShowUpdatedTimeFilter(false);
          }}
        />
      ),
      filterIcon: (
        <SearchOutlined style={{ color: queryFilter.updatedTime.length ? '#38b2a6' : '#c8c9cb' }} />
      ),
      filterDropdownVisible: showUpdatedTimeFilter,
      onFilterDropdownVisibleChange: view => {
        setShowUpdatedTimeFilter(view);
      },
    },
    {
      title: t('cms.referral.payout.list.column.status'),
      dataIndex: 'status',
      key: 'status',
      className: 'payout-list__body__list__column',
      width: 120,
      render: text => {
        return (
          <Tag
            className={classNames('payout-list__body__list__tag', {
              [classes.tagNew]: text === Statuses.New,
              [classes.tagPending]: text === Statuses.Pending,
              [classes.tagApproved]: text === Statuses.Approved,
              [classes.tagSuccess]: text === Statuses.Settled,
              [classes.tagFailed]: text === Statuses.Deducted,
              [classes.tagInvalid]: [Statuses.Invalid, Statuses.Cancelled].includes(text),
            })}
          >
            {text ? t(`cms.referral.payout.list.column.status.${text.toLowerCase()}`) : '-'}
          </Tag>
        );
      },
      ...(selectTab === 'all'
        ? {
            filterMultiple: true,
            filters: [
              {
                text: t('cms.referral.payout.list.column.status.new'),
                value: Statuses.New,
              },
              {
                text: t('cms.referral.payout.list.column.status.pending_approval'),
                value: Statuses.Pending,
              },
              {
                text: t('cms.referral.payout.list.column.status.approved'),
                value: Statuses.Approved,
              },
              {
                text: t('cms.referral.payout.list.column.status.settled'),
                value: Statuses.Settled,
              },
              {
                text: t('cms.referral.payout.list.column.status.cancelled'),
                value: Statuses.Cancelled,
              },
              {
                text: t('cms.referral.payout.list.column.status.deducted'),
                value: Statuses.Deducted,
              },
              {
                text: t('cms.referral.payout.list.column.status.invalid'),
                value: Statuses.Invalid,
              },
            ],
            filteredValue: queryFilter.status || [],
            filterIcon: (
              <FilterOutlined
                style={{ color: queryFilter.status.length ? '#38b2a6' : '#c8c9cb' }}
              />
            ),
          }
        : {}),
    },
    ...(selectTab === 'all'
      ? [
          {
            title: t('cms.referral.payout.list.column.secondary_status'),
            dataIndex: 'secondaryStatus',
            key: 'secondaryStatus',
            className: 'payout-list__body__list__column',
            ellipsis: true,
            width: 250,
            render: (text, node) => {
              return (
                <span className="payout-list__body__list__column-body">
                  <Choose>
                    <When
                      condition={[
                        SecondaryStatus.ManualCancel,
                        SecondaryStatus.ManualDeduct,
                      ].includes(text)}
                    >
                      <Tooltip placement="top" arrowPointAtCenter title={node.cancelReason}>
                        <span className="payout-list--text-underline">
                          {t(
                            SecondaryStatusLabel[
                              Object.keys(SecondaryStatusLabel)[
                                Object.values(SecondaryStatus).indexOf(text)
                              ]
                            ],
                          )}
                        </span>
                      </Tooltip>
                    </When>
                    <When condition={text}>
                      {t(
                        SecondaryStatusLabel[
                          Object.keys(SecondaryStatusLabel)[
                            Object.values(SecondaryStatus).indexOf(text)
                          ]
                        ],
                      )}
                    </When>
                    <Otherwise>-</Otherwise>
                  </Choose>
                </span>
              );
            },
            filterMultiple: true,
            filters: [
              {
                text: t('cms.referral.payout.list.column.secondary_status.booking_cancelled'),
                value: SecondaryStatus.BookingCancelled,
              },
              {
                text: t(
                  'cms.referral.payout.list.column.secondary_status.without_booking_commission',
                ),
                value: SecondaryStatus.WithoutBookingCommission,
              },
            ],
            filteredValue: queryFilter.secondaryStatus || [],
            filterIcon: (
              <FilterOutlined
                style={{ color: queryFilter.secondaryStatus.length ? '#38b2a6' : '#c8c9cb' }}
              />
            ),
          },
        ]
      : []),
    {
      title: t('cms.referral.payout.list.column.create_time'),
      dataIndex: 'createdAt',
      key: 'createTime',
      className: 'payout-list__body__list__column',
      ellipsis: true,
      // width: 140,

      render: text => {
        return (
          <span style={{ minWidth: 140 }} className="payout-list__body__list__column-body">
            {formatDay(dayFormatType.UKWITHHM, text) || '-'}
          </span>
        );
      },
    },
    ...(selectTab === 'pending' && role.slug !== 'bd'
      ? [
          {
            title: t('cms.referral.payout.list.column.actions'),
            dataIndex: 'actions',
            key: 'actions',
            fixed: 'right',
            className:
              'payout-list__body__list__column--center payout-list__body__list__column--h-22',
            width: 100,
            render: (_text, record) => (
              <div>
                <div
                  className="payout-list__body__list__column-body payout-list__body__list__column-body--center payout-list__body__list__column-body--h-22 payout-list--mr-26"
                  style={{ minWidth: 'unset' }}
                >
                  <PayoutActionButton
                    id={record.id}
                    isPending={record.status === Statuses.Pending}
                    handleSubmit={auditPayout}
                  />
                </div>

                <div
                  className="payout-list__body__list__column-body payout-list__body__list__column-body--center payout-list__body__list__column-body--h-22"
                  style={{ minWidth: 'unset' }}
                >
                  <PayoutActionRemoveButton
                    handleClick={() =>
                      setShowPayoutCancelReasonEditor({
                        id: record.bookingId,
                        bookingJourney: record.bookingJourney,
                      })
                    }
                    isRemovable={true}
                  />
                </div>
              </div>
            ),
          },
        ]
      : []),
  ];

  const handleClosePayoutOppsViewerModal = () => {
    setShowPayoutOppsViewer(null);
  };

  const handleClosePayoutCancelReasonModal = () => {
    setShowPayoutCancelReasonEditor(null);
  };

  const handleExportCsv = () => {
    exportToCSV({
      query: QUERY_PARTNER_PAYOUT,
      variables: {
        pageNumber: 1,
        pageSize: payoutListTotalCount,
        sortBy: queryFilter.sortBy,
        sortDirection: queryFilter.sortDirection,
        statuses: [...new Set(StatusesEnum[selectTab].concat(queryFilter.status))],
        secondaryStatuses: queryFilter.secondaryStatus,
        partnerIds: queryFilter.partnerId.length ? queryFilter.partnerId : null,
        partnerCodes: queryFilter.partnerCode ? [queryFilter.partnerCode] : null,
        opportunityReferenceIds: queryFilter.oppsNumber ? [queryFilter.oppsNumber] : null,
        updatedAtMin: queryFilter.updatedTime.length ? queryFilter.updatedTime[0] : null,
        updatedAtMax: queryFilter.updatedTime.length ? queryFilter.updatedTime[1] : null,
        countryIds: queryFilter.countryIds.length ? queryFilter.countryIds : null,
        paymentPreference: queryFilter?.paymentPreference?.length
          ? queryFilter?.paymentPreference[0]
          : null,
        stages: queryFilter?.stage,
      },
      fetchPolicy: 'no-cache',
    });
  };

  return (
    <>
      <div className="payout-list">
        <div className="payout-list__header">
          <h1>{t('cms.referral.payout.title')}</h1>
        </div>

        <div className="payout-list__header__menu">
          <div className="payout-list__header__table-tabs">
            <Tabs activeKey={selectTab} onChange={handleTableChange}>
              <Tabs.TabPane tab={t('cms.referral.payout.tab.all_payouts')} key="all" />
              <Tabs.TabPane tab={t('cms.referral.payout.tab.pending_approval')} key="pending" />
              <Tabs.TabPane tab={t('cms.referral.payout.tab.settlement_history')} key="history" />
            </Tabs>
          </div>
          <div className="payout-list__header__button-group">
            <If condition={selectTab === 'pending' && selectedRows.length && role.slug !== 'bd'}>
              <PayoutBunchActionButton
                ids={selectedRows}
                handleSubmit={auditPayout}
                onSuccess={() => setSelectedRows([])}
                selectedItems={selectedRowsRaw.current}
              />
            </If>

            <If condition={selectTab === 'all'}>
              <button
                type="button"
                className={classNames(
                  'payout-list__header__button  payout-list__header__button--default',
                  {
                    'payout-list__header__button--disabled': isExportPayoutLoading,
                  },
                )}
                onClick={handleExportCsv}
              >
                <Choose>
                  <When condition={isExportPayoutLoading}>
                    <Spin
                      className="payout-list__header__button__icon"
                      indicator={<Icon type="loading" style={{ fontSize: 14 }} spin />}
                    />
                  </When>
                  <Otherwise>
                    <Icon className="payout-list__header__button__icon" type="export" />
                  </Otherwise>
                </Choose>

                {t('cms.referral.partner.header.btn.export')}
              </button>
            </If>

            <button
              className="payout-list__header__button  payout-list__header__button--default"
              type="button"
              onClick={() => setShowSettlementListDrawer(current => !current)}
            >
              <PayoutLIstBtnSettlementIcon className="payout-list__header__button__icon" />
              {t('cms.referral.payout.btn.settlement_list')}
            </button>
          </div>
        </div>

        <section
          className={classNames('payout-list__body', {
            'payout-list__body--bg-white': payoutList.length === 0 && !loading,
          })}
        >
          <Choose>
            <When condition={loading && !isValidFilter && payoutList.length === 0}>
              <div
                className={classNames('payout-list__spin-container', {
                  'payout-list--w-7': showSettlementListDrawer,
                })}
              >
                <Spin />
              </div>
            </When>
            <When condition={payoutList.length || isValidFilter}>
              <Table
                columns={tableColumns}
                size="middle"
                rowKey={record => record.id}
                className={classNames('table-list payout-list__body__list', {
                  'payout-list--w-7': showSettlementListDrawer,
                })}
                dataSource={payoutList}
                {...(selectTab === 'pending' && role.slug !== 'bd'
                  ? {
                      rowSelection: {
                        selectedRowKeys: selectedRows,
                        onChange: handleRowSelect,
                        getCheckboxProps: record => ({
                          disabled: record.status === Statuses.Approved,
                        }),
                        columnWidth: 60,
                      },
                    }
                  : null)}
                pagination={{
                  current: pagination.pageNumber,
                  pageSize: pagination.pageSize,
                  size: 'middle',
                  showSizeChanger: true,
                  hideOnSinglePage: false,
                  total: payoutListTotalCount,
                }}
                loading={loading}
                scroll={{ x: true }}
                onChange={(newestPagination, filter, sorter) => {
                  setPagination({
                    pageNumber: newestPagination.current,
                    pageSize: newestPagination.pageSize,
                  });
                  setQueryFilter(current => ({
                    ...current,
                    status: filter.status || [],
                    countryIds: filter.country || [],
                    secondaryStatus: filter.secondaryStatus || [],
                    currencies: filter.currency || [],
                    paymentPreference: filter.paymentPreference,
                    stage: filter.stage,
                    ...(sorter?.columnKey === 'payoutAmount'
                      ? {
                          sortBy: 'AMOUNT',
                          sortDirection: sortDirectionMapping[sorter.order],
                        }
                      : {
                          sortBy: PayoutRecordSortBy.UpdatedAt,
                          sortDirection: 'DESCENDING',
                        }),
                  }));
                }}
              />
            </When>
            <When condition={payoutList.length === 0 && !isValidFilter}>
              <div
                className={classNames('payout-list__blank-tips', {
                  'payout-list--w-7': showSettlementListDrawer,
                })}
              >
                <div className="payout-list__blank-tips__content">
                  <div>
                    <PayoutEmptyIcon />
                  </div>
                  <span className="payout-list__blank-tips__tip">
                    <Choose>
                      <When condition={selectTab === 'all'}>
                        {t('cms.referral.payout.blanktip')}
                      </When>
                      <When condition={selectTab === 'pending'}>
                        {t('cms.referral.payout.blanktip.pending')}
                      </When>
                      <When condition={selectTab === 'history'}>
                        {t('cms.referral.payout.blanktip.history')}
                      </When>
                    </Choose>
                  </span>
                </div>
              </div>
            </When>
          </Choose>

          {showSettlementListDrawer ? (
            <SettlementList
              visiable={showSettlementListDrawer}
              role={role}
              onClose={() => setShowSettlementListDrawer(false)}
            />
          ) : null}
        </section>

        <If condition={showPayoutOppsViewer}>
          <PayoutOppsViewer
            activeModal
            id={showPayoutOppsViewer.id}
            bookingReferenceIds={showPayoutOppsViewer.bookingReferenceIds}
            bookingJourney={showPayoutOppsViewer.bookingJourney}
            t={t}
            handleCloseModal={handleClosePayoutOppsViewerModal}
          />
        </If>

        <If condition={showPayoutCancelReasonEditor}>
          <PayoutCancelReasonEditor
            activeModal
            id={showPayoutCancelReasonEditor.id}
            bookingJourney={showPayoutCancelReasonEditor.bookingJourney}
            t={t}
            handleCloseModal={handleClosePayoutCancelReasonModal}
          />
        </If>
      </div>
    </>
  );
};

const PayoutActionButton = ({ id, isPending, handleSubmit }) => {
  const [loading, setLoading] = useState(false);
  const antIcon = <Icon type="loading" style={{ fontSize: 14 }} spin />;
  const { t } = useTranslation();

  const handleClick = async () => {
    try {
      setLoading(true);
      const submitResponse = await handleSubmit({
        variables: {
          ids: [id],
          operationType: isPending ? OperationType.Approve : OperationType.Unapproved,
        },
      });
      if (submitResponse?.data) {
        message.success(
          isPending
            ? t('cms.referral.payout.action.apporve.toast')
            : t('cms.referral.payout.action.unapporve.toast'),
        );
      }
    } catch (error) {
      message.error(error.toString());
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      className={classNames('payout-list__btn payout-list__btn__list-action', {
        'payout-list__btn--disabled': loading,
        'payout-list__btn--approved': !isPending,
      })}
      data-testid={isPending ? 'approve-btn' : 'unApprove-btn'}
      disabled={loading}
      onClick={handleClick}
    >
      <Choose>
        <When condition={loading}>
          <Spin indicator={antIcon} />
        </When>
        <When condition={isPending}>
          <PayoutListActionAddIcon />
        </When>
        <When condition={!isPending}>
          <PayoutListActionRemoveIcon />
        </When>
      </Choose>
    </button>
  );
};

const PayoutActionRemoveButton = ({ handleClick, isRemovable }) => {
  return (
    <button
      className={classNames('payout-list__btn payout-list__btn__list-action', {
        'payout-list__btn--disabled': !isRemovable,
      })}
      disabled={!isRemovable}
      onClick={handleClick}
    >
      <PayoutListActionCancelIcon color={isRemovable ? '#38B2A6' : '#BFBFBF'} />
    </button>
  );
};

const PayoutBunchActionButton = ({ ids, handleSubmit, onSuccess, selectedItems }) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const isDisabled = useMemo(() => {
    return loading || !ids.length;
  }, [loading, ids]);

  const antIcon = (
    <Icon
      type="loading"
      className="payout-list__header__button__icon"
      style={{ fontSize: 14, color: 'white' }}
      spin
    />
  );

  function checkIsSameCurrency() {
    const uniqCurrencyId = [...new Set(selectedItems.map(item => item.currency))];
    const uniqCountryId = [...new Set(selectedItems.map(item => item.countryId))];
    if (uniqCurrencyId.length > 1 || uniqCountryId.length > 1) {
      Modal.error({
        title: t('cms.referral.payout.opps.modal.btn.add'),
        content: t('cms.referral.payout.action.approve.mix_currency.toast'),
      });
      return false;
    }
    return true;
  }

  const handleClick = async () => {
    try {
      setLoading(true);
      if (checkIsSameCurrency()) {
        const submitResponse = await handleSubmit({
          variables: {
            ids: ids,
            operationType: OperationType.Approve,
          },
        });
        if (submitResponse?.data) {
          message.success(t('cms.referral.payout.action.apporve.toast'));
          onSuccess();
        }
      }
    } catch (error) {
      message.error(error.toString());
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      type="button"
      className={classNames('payout-list__header__button payout-list__header__button--primary', {
        'payout-list__header__button--disabled': isDisabled,
        'payout-list__header__button--loading': loading,
      })}
      disabled={isDisabled}
      onClick={handleClick}
    >
      <If condition={loading}>
        <Spin indicator={antIcon} />
      </If>
      {t('cms.referral.payout.btn.add_to_settlement_list')}
    </button>
  );
};

export default Partner;
